/**
 * Error Handler Utilities
 * 
 * Provides utilities for transforming AWS SDK errors into user-friendly messages
 * and logging errors with appropriate detail levels.
 * 
 * Features:
 * - Transform AWS SDK errors to readable messages
 * - Handle common error types (throttling, access denied, not found)
 * - Log errors with context for debugging
 * 
 * Requirements: 13.1, 13.4, 15.5
 */

/**
 * AWS error types that we handle specifically
 */
type AWSErrorCode =
    | 'ThrottlingException'
    | 'RequestLimitExceeded'
    | 'TooManyRequestsException'
    | 'AccessDeniedException'
    | 'UnauthorizedException'
    | 'InvalidAccessKeyId'
    | 'SignatureDoesNotMatch'
    | 'ResourceNotFoundException'
    | 'NoSuchKey'
    | 'NoSuchBucket'
    | 'QueueDoesNotExist'
    | 'ValidationException'
    | 'InvalidParameterException'
    | 'NetworkingError'
    | 'TimeoutError';

/**
 * User-friendly error message
 */
export interface UserFriendlyError {
    message: string;
    retryable: boolean;
    category: 'throttling' | 'auth' | 'not_found' | 'validation' | 'network' | 'unknown';
}

/**
 * Transforms AWS SDK errors into user-friendly messages
 * 
 * This function takes raw AWS SDK errors and converts them into
 * messages that are appropriate to display to end users, while
 * preserving technical details in console logs for debugging.
 * 
 * @param error - The error object from AWS SDK
 * @param operation - Description of the operation that failed (e.g., "upload file", "create job")
 * @returns User-friendly error object with message and retry guidance
 * 
 * Requirements: 13.1, 15.5
 */
export function transformAWSError(error: unknown, operation: string): UserFriendlyError {
    // Handle non-Error objects
    if (!(error instanceof Error)) {
        return {
            message: `Failed to ${operation}. Please try again.`,
            retryable: true,
            category: 'unknown',
        };
    }

    // Extract error code from AWS SDK error
    const awsError = error as Error & { name?: string; code?: string; $metadata?: { httpStatusCode?: number } };
    const errorCode = (awsError.name || awsError.code) as AWSErrorCode | undefined;
    const statusCode = awsError.$metadata?.httpStatusCode;

    // Handle throttling errors
    if (
        errorCode === 'ThrottlingException' ||
        errorCode === 'RequestLimitExceeded' ||
        errorCode === 'TooManyRequestsException' ||
        statusCode === 429
    ) {
        return {
            message: `Too many requests. Please wait a moment and try again.`,
            retryable: true,
            category: 'throttling',
        };
    }

    // Handle authentication/authorization errors
    if (
        errorCode === 'AccessDeniedException' ||
        errorCode === 'UnauthorizedException' ||
        errorCode === 'InvalidAccessKeyId' ||
        errorCode === 'SignatureDoesNotMatch' ||
        statusCode === 401 ||
        statusCode === 403
    ) {
        return {
            message: `Access denied. Please check your credentials and try signing in again.`,
            retryable: false,
            category: 'auth',
        };
    }

    // Handle resource not found errors
    if (
        errorCode === 'ResourceNotFoundException' ||
        errorCode === 'NoSuchKey' ||
        errorCode === 'NoSuchBucket' ||
        errorCode === 'QueueDoesNotExist' ||
        statusCode === 404
    ) {
        return {
            message: `Resource not found. The requested item may have been deleted or does not exist.`,
            retryable: false,
            category: 'not_found',
        };
    }

    // Handle validation errors
    if (
        errorCode === 'ValidationException' ||
        errorCode === 'InvalidParameterException' ||
        statusCode === 400
    ) {
        return {
            message: `Invalid request. Please check your input and try again.`,
            retryable: false,
            category: 'validation',
        };
    }

    // Handle network errors
    if (
        errorCode === 'NetworkingError' ||
        errorCode === 'TimeoutError' ||
        error.message.includes('network') ||
        error.message.includes('timeout') ||
        error.message.includes('ECONNREFUSED') ||
        error.message.includes('ETIMEDOUT')
    ) {
        return {
            message: `Network error. Please check your connection and try again.`,
            retryable: true,
            category: 'network',
        };
    }

    // Handle server errors (5xx)
    if (statusCode && statusCode >= 500) {
        return {
            message: `Server error. Please try again in a few moments.`,
            retryable: true,
            category: 'unknown',
        };
    }

    // Default error message
    return {
        message: `Failed to ${operation}. ${error.message || 'Please try again.'}`,
        retryable: true,
        category: 'unknown',
    };
}

/**
 * Logs an error with context for debugging
 * 
 * This function logs errors to the console with appropriate detail
 * for debugging while avoiding logging sensitive information.
 * 
 * @param error - The error object
 * @param context - Context about where/when the error occurred
 * @param metadata - Additional metadata to log (will be sanitized)
 * 
 * Requirements: 13.4
 */
export function logError(
    error: unknown,
    context: string,
    metadata?: Record<string, unknown>
): void {
    // Sanitize metadata to remove sensitive fields
    const sanitizedMetadata = metadata ? sanitizeMetadata(metadata) : undefined;

    // Log error with context
    console.error(`[Error] ${context}:`, {
        error: error instanceof Error ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
        } : error,
        metadata: sanitizedMetadata,
        timestamp: new Date().toISOString(),
    });
}

/**
 * Sanitizes metadata to remove sensitive information before logging.
 *
 * Recursively walks the metadata object and replaces any value whose key
 * contains a sensitive substring (e.g., "password", "token", "secretkey")
 * with `'[REDACTED]'`. This prevents credentials from leaking into
 * console logs or error reporting services.
 *
 * @param metadata - Raw metadata object
 * @returns Sanitized metadata safe for logging
 */
function sanitizeMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
    // Keys (case-insensitive) that indicate sensitive values
    const sensitiveKeys = [
        'password',
        'secret',
        'token',
        'accesskey',
        'secretkey',
        'sessiontoken',
        'authorization',
        'apikey',
        'credentials',
    ];

    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(metadata)) {
        const lowerKey = key.toLowerCase();

        // Check if key contains sensitive information
        if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
            sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
            // Recursively sanitize nested objects
            sanitized[key] = sanitizeMetadata(value as Record<string, unknown>);
        } else {
            sanitized[key] = value;
        }
    }

    return sanitized;
}

/**
 * Checks if an error is retryable
 * 
 * @param error - The error to check
 * @returns true if the error is retryable
 */
export function isRetryableError(error: unknown): boolean {
    const transformed = transformAWSError(error, 'operation');
    return transformed.retryable;
}

/**
 * Gets a user-friendly error message from any error
 * 
 * @param error - The error object
 * @param operation - Description of the operation that failed
 * @returns User-friendly error message
 */
export function getErrorMessage(error: unknown, operation: string): string {
    const transformed = transformAWSError(error, operation);
    return transformed.message;
}
